import React, { useEffect, useMemo, useState } from 'react'
import {
  Card,
  CardBody,
  Container,
  Input,
  Label,
  Alert,
  Spinner,
  FormGroup,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from 'reactstrap'
import DataTable, { TableColumn } from 'react-data-table-component'
import Loader from '../../../Components/Widgets/Loader/generic'
import { get, postJson } from '../../../hook/request'
import { toast } from 'react-toastify'

type BollaCaricata = {
  ID: number
  codice: string
  data_ins: string
}

type ReportRow = {
  bollaProdottoID: number
  IDBolla: number
  CodiceBolla: string
  style_code: string
  name: string
  variante_code: string
  colonna: number
  qta: number
  scanzionati: number
  formato: 'NUM' | 'ALFA'
   taglia: string
}



function unwrapPayload(res: any) {
  return res?.data?.data ?? res?.data ?? res
}

export default function ReportBolle() {
  const [bolle, setBolle] = useState<BollaCaricata[]>([])
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [daControllare, setDaControllare] = useState(false)
  const [controlloGiacenze, setControlloGiacenze] = useState(false)


  const [loadingBolle, setLoadingBolle] = useState(false)
  const [loadingReport, setLoadingReport] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [report, setReport] = useState<ReportRow[]>([])
  const [filtroCodice, setFiltroCodice] = useState('')


  // --- MODAL EDIT ---
  const [editOpen, setEditOpen] = useState(false)
  const [editLoading, setEditLoading] = useState(false)
  const [editRow, setEditRow] = useState<ReportRow | null>(null)
  const [editQta, setEditQta] = useState<number>(0)
  const [editScanz, setEditScanz] = useState<number>(0)


  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 720px)') 
    const onChange = () => setIsMobile(mq.matches)
    onChange()
    mq.addEventListener?.('change', onChange)
    return () => mq.removeEventListener?.('change', onChange)
  }, [])



  const openEdit = (row: ReportRow) => {
    setEditRow(row)
    setEditQta(Number(row.qta) || 0)
    setEditScanz(Number(row.scanzionati) || 0)
    setEditOpen(true)
  }

  const closeEdit = () => {
    if (editLoading) return
    setEditOpen(false)
    setEditRow(null)
  }

  const saveEdit = async () => {
    if (!editRow) return
    const qta = Math.max(0, Number(editQta) || 0)
    const sc = Math.max(0, Number(editScanz) || 0)

    try {
      setEditLoading(true)

      const res = await postJson('/bolle/prodottoEdit', {
        ID: editRow.bollaProdottoID,
        qta,
        scanzionati: sc,
      })

      const payload = unwrapPayload(res)
      const status = String(payload?.status ?? 'ok').toLowerCase()
      const message = String(payload?.message ?? 'Aggiornato').trim()

      if (status === 'ok') {
        toast.success(message || 'Aggiornato')
        setReport(prev =>
          prev.map(r =>
            r.bollaProdottoID === editRow.bollaProdottoID
              ? { ...r, qta, scanzionati: sc }
              : r
          )
        )
        setEditOpen(false)
        setEditRow(null)
      } else {
        toast.error(message || 'Operazione non riuscita')
      }
    } catch (e: any) {
      const payload = unwrapPayload(e?.response)
      toast.error(String(payload?.message ?? e?.message ?? 'Errore di rete').trim())
    } finally {
      setEditLoading(false)
    }
  }

  const loadBolle = async () => {
    setError(null)
    setLoadingBolle(true)
    try {
      const res = await get<any>('/bolla/caricati?aperto=on')
      const payload = unwrapPayload(res)
      const list = Array.isArray(payload) ? payload : Array.isArray(payload?.list) ? payload.list : []
      setBolle(list)
      if (list.length > 0) setSelectedId(prev => (prev == null ? list[0].ID : prev))
      else {
        setSelectedId(null)
        setReport([])
      }
    } catch {
      setError('Impossibile caricare le bolle.')
      setBolle([])
      setSelectedId(null)
      setReport([])
    } finally {
      setLoadingBolle(false)
    }
  }

  const loadReport = async (id: number, scanOff: boolean, ctrlGi: boolean) => {
    setError(null)
    setLoadingReport(true)
    try {
      const qs = new URLSearchParams()
      qs.set('ID', String(id))
      if (scanOff) qs.set('scan', 'off')

      //controllo  quelli non scansionati e quelli che hanno scansione > di qta
      if (ctrlGi) qs.set('ctrl_gi', 'ok')

      const res = await get<any>(`/bolla/report?${qs.toString()}`)
      const payload = unwrapPayload(res)

      const status = String(payload?.status ?? '').toLowerCase()
      const list = Array.isArray(payload?.lista)
        ? payload.lista
        : Array.isArray(payload?.list)
          ? payload.list
          : Array.isArray(payload)
            ? payload
            : []

      if (status === 'ok' || Array.isArray(list)) {
        setReport(list as ReportRow[])
      } else {
        setError(String(payload?.message ?? 'Operazione non riuscita').trim())
        setReport([])
      }
    } catch (e: any) {
      const payload = unwrapPayload(e?.response)
      setError(String(payload?.message ?? e?.message ?? 'Errore di rete').trim())
      setReport([])
    } finally {
      setLoadingReport(false)
    }
  }

  useEffect(() => {
    loadBolle()
  }, [])

  useEffect(() => {
    if (selectedId != null) loadReport(selectedId, daControllare, controlloGiacenze)
  }, [selectedId, daControllare, controlloGiacenze])

  const filteredReport = useMemo(() => {
    const q = filtroCodice.trim().toLowerCase()
    if (!q) return report
    return report.filter(r =>
      `${r.style_code ?? ''} ${r.name ?? ''}`.toLowerCase().includes(q)
    )
  }, [report, filtroCodice])

  const columns: TableColumn<ReportRow>[] = useMemo(() => {
    if (isMobile) {
      return [
        {
          name: 'Dettaglio',
          cell: row => (
            <div className="w-100 d-flex justify-content-between align-items-start gap-2">
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, lineHeight: 1.2, wordBreak: 'break-word' }}>
                  {row.style_code} {row.variante_code} - {row.name}
                </div>
                <div className="text-muted" style={{ fontSize: 12, lineHeight: 1.2 }}>
                  Taglia: {row.taglia} • Qta: {row.qta} • Scans: {row.scanzionati}
                </div>
              </div>

              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={e => {
                  e.stopPropagation()
                  openEdit(row)
                }}
                aria-label="Modifica"
              >
                <i className="fa-solid fa-pen-to-square" />
              </button>
            </div>
          ),
          sortable: true,
          sortFunction: (a, b) =>
            `${a.style_code} ${a.variante_code}`.localeCompare(`${b.style_code} ${b.variante_code}`),
          grow: 1,
          wrap: true,
        },
      ]
    }

    return [
      {
        name: 'Codice - Variante',
        selector: r => `${r.style_code} ${r.variante_code}`,
        sortable: true,
        width: '200px',
        wrap: true,
      },
      {
        name: 'Modello',
        selector: r => `${r.name}`,
        sortable: true,
        minWidth: '240px',
        wrap: true,
      },
      {
        name: 'Taglia',
        selector: r => String(r.taglia ?? ''),
        sortable: true,
        width: '90px',
      },
      { name: 'Qta', selector: r => String(r.qta), sortable: true, width: '80px' },
      { name: 'Scans.', selector: r => String(r.scanzionati), sortable: true, width: '90px' },
      {
        name: '',
        cell: row => (
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={e => {
              e.stopPropagation()
              openEdit(row)
            }}
          >
            <i className="fa-solid fa-pen-to-square" />
          </button>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
        width: '110px',
      },
    ]
  }, [isMobile])


  return (
    <Container fluid className="p-3">
      <Card>
        <CardBody>
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
            <div>
              <h3 className="mb-1">Report Bolle</h3>
            </div>

            <div style={{ minWidth: 280 }}>
              <Label className="mb-1">Bolla caricata</Label>
              <Input
                type="select"
                value={selectedId ?? ''}
                disabled={loadingBolle || bolle.length === 0}
                onChange={e => setSelectedId(e.target.value ? Number(e.target.value) : null)}
              >
                {bolle.length === 0 ? (
                  <option value="">Nessuna bolla</option>
                ) : (
                  bolle.map(b => (
                    <option key={b.ID} value={b.ID}>
                      {b.codice}
                    </option>
                  ))
                )}
              </Input>

              <FormGroup check className="mt-2">
                <Input
                  id="daControllare"
                  type="checkbox"
                  checked={daControllare}
                  onChange={e => setDaControllare(e.target.checked)}
                  disabled={selectedId == null || loadingReport}
                />
                <Label for="daControllare" check>
                  Non scansionati
                </Label>
              </FormGroup>

              <FormGroup check className="mt-2">
                <Input
                  id="controllo_giacenze"
                  type="checkbox"
                  checked={controlloGiacenze}
                  onChange={e => setControlloGiacenze(e.target.checked)}
                  disabled={selectedId == null || loadingReport}
                />
                <Label for="controllo_giacenze" check>
                  Controllo giacenze
                </Label>
              </FormGroup>

            </div>
          </div>

          <div className="mt-3">
            {error && (
              <Alert color="danger" className="mb-3">
                {error}
              </Alert>
            )}

            {loadingBolle && (
              <div className="d-flex align-items-center gap-2">
                <Spinner size="sm" /> <span>Caricamento bolle…</span>
              </div>
            )}

            {!loadingBolle && bolle.length === 0 && (
              <Alert color="warning" className="mb-0">
                Nessuna bolla aperta.
              </Alert>
            )}

            {selectedId != null && (
              <div className="mt-3">
                <div className="d-flex align-items-end justify-content-between flex-wrap gap-2 mb-2">
                  <div style={{ minWidth: 320 }}>
                    <Label className="mb-1">Filtro per codice</Label>
                    <Input
                      style={{padding:'5px 5px'}}
                      value={filtroCodice}
                      onChange={e => setFiltroCodice(e.target.value)}
                      placeholder="Inserire un codice o modello"
                      disabled={loadingReport}
                    />
                  </div>

                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setFiltroCodice('')}
                    disabled={!filtroCodice}
                  >
                    Pulisci
                  </button>
                </div>

                {/* ✅ wrapper per scroll orizzontale su mobile */}
                <DataTable<ReportRow>
                  data={filteredReport}
                  columns={columns}
                  keyField="bollaProdottoID"
                  progressPending={loadingReport}
                  progressComponent={<Loader />}
                  pagination
                  paginationPerPage={isMobile ? 25 : 50}
                  paginationRowsPerPageOptions={isMobile ? [25, 50, 100] : [50, 100, 200]}
                  striped
                  highlightOnHover
  
                  className="display"
                  conditionalRowStyles={[
                    {
                      when: row => Number(row.qta) == Number(row.scanzionati),
                      style: { backgroundColor: 'rgba(21, 219, 68, 0.22)' },
                    },
                    {
                      when: row => Number(row.qta) < Number(row.scanzionati),
                      style: { backgroundColor: 'rgba(206, 219, 21, 0.26)' },
                    },
                    {
                      when: row => Number(row.qta) > Number(row.scanzionati),
                      style: { backgroundColor: 'rgba(220, 53, 70, 0.22)' },
                    },
                  ]}
                  noDataComponent={loadingReport ? 'Caricamento…' : 'Nessuna riga per questa bolla.'}
                />

              </div>
            )}
          </div>

          {/* MODAL EDIT */}
          <Modal isOpen={editOpen} toggle={closeEdit} centered>
            <ModalHeader toggle={closeEdit}>Modifica riga</ModalHeader>
            <ModalBody>
              {editRow && (
                <div className="mb-3">
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    {editRow.style_code} {editRow.variante_code} — Taglia {editRow.taglia}
                  </div>
                </div>
              )}

              <div className="mb-3">
                <Label className="mb-1">Qta</Label>
                <Input
                  type="number"
                  min={0}
                  step={1}
                  value={editQta}
                  onChange={e => setEditQta(Number(e.target.value))}
                  disabled={editLoading}
                />
              </div>

              <div>
                <Label className="mb-1">Scansionati</Label>
                <Input
                  type="number"
                  min={0}
                  step={1}
                  value={editScanz}
                  onChange={e => setEditScanz(Number(e.target.value))}
                  disabled={editLoading}
                />
              </div>
            </ModalBody>
            <ModalFooter className="d-flex justify-content-between">
              <Button color="light" style={{ color: 'black' }} onClick={closeEdit} disabled={editLoading}>
                Annulla
              </Button>
              <Button color="primary" onClick={saveEdit} disabled={editLoading || !editRow}>
                {editLoading ? <Spinner size="sm" /> : 'Salva'}
              </Button>
            </ModalFooter>
          </Modal>
        </CardBody>
      </Card>
    </Container>
  )
}
